import React from "react";

/**
 * SectionHeader — RC4 shared section header component.
 *
 * Unifies the mid-page section heading style across Dashboard,
 * Customers/Clients, Tours, Drivers, Quotes, Contracts, Documents,
 * Dispatch, Reports, Administration, and Fleet.
 *
 * The canonical style MATCHES the Dispatch RC1 step-5 improvement
 * (commit 87ab67b): the bucket header was bumped 10.5px → 11.5px
 * (+9.5%), font-normal → font-semibold, text-steel → text-carbon on
 * road-white — a WCAG-AA-safe contrast bump. Every SectionHeader on
 * the platform now uses that same token.
 *
 * Two variants:
 *   default   — the canonical Dispatch-style eyebrow heading
 *               (`text-[11.5px] uppercase tracking-widest font-semibold
 *                text-carbon`). Suitable for "Tour Information",
 *               "Documents", "Assigned Drivers", "Insurance", etc.
 *   page      — larger route-level h2 (`text-lg sm:text-xl`
 *               font-semibold tracking-tight). Suitable for a single
 *               top-of-workspace heading when PageHeader is not used.
 *
 * NEVER enlarges: form field labels, table cell text, body copy,
 * buttons. Those keep their existing sizing.
 */
export function SectionHeader({
  children,
  right = null,
  withDivider = false,   // default off — canonical eyebrow has no divider
  variant = "default",   // "default" | "page"
  className = "",
  testid,
  as: Tag = "h2",
}) {
  const isPage = variant === "page";
  const headingCls = isPage
    ? "aa-section-header text-lg sm:text-xl font-semibold tracking-tight text-carbon dark:text-road-white leading-tight truncate"
    : "aa-section-header text-[11.5px] uppercase tracking-widest font-semibold text-carbon dark:text-road-white truncate";
  return (
    <div
      className={
        "flex items-center justify-between gap-3 " +
        (isPage ? "mb-3 " : "mb-2 ") +
        (withDivider ? "pb-2 border-b border-coach-silver " : "") +
        className
      }
      data-testid={testid}
    >
      <Tag className={headingCls}>{children}</Tag>
      {right && <div className="shrink-0">{right}</div>}
    </div>
  );
}

export default SectionHeader;
